import React, { useEffect, useState } from 'react';
import { Pie, Doughnut, Bar } from 'react-chartjs-2';
import 'chart.js/auto';

// Dummy API fetchers (replace with real endpoints as needed)
export async function fetchDemographics(query) {
  // Replace with your real API endpoint
  const url = `https://ai.oigetit.com/AI71/Demographics?json=${encodeURIComponent(JSON.stringify({ Query: query }))}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error('Failed to fetch demographics');
  return await res.json();
}
async function fetchLanguages(query) {
  const url = `https://ai.oigetit.com/AI71/Languages?json=${encodeURIComponent(JSON.stringify({ Query: query }))}`;
  const res = await fetch(url);
  if (!res.ok) {
    console.error('Languages API error:', res.status, await res.text());
    return { languages: [] }; // fallback to empty
  }
  let json;
  try {
    json = await res.json();
  } catch (e) {
    console.error('Languages API invalid JSON:', e);
    return { languages: [] };
  }
  if (!json.languages || !Array.isArray(json.languages)) {
    console.error('Languages API unexpected format:', json);
    return { languages: [] };
  }
  return json;
}
async function fetchInterests(query) {
  const url = `https://ai.oigetit.com/AI71/Interests?json=${encodeURIComponent(JSON.stringify({ Query: query }))}`;
  const res = await fetch(url);
  if (!res.ok) {
    console.error('Interests API error:', res.status, await res.text());
    return { interests: [] }; // fallback to empty
  }
  let json;
  try {
    json = await res.json();
  } catch (e) {
    console.error('Interests API invalid JSON:', e);
    return { interests: [] };
  }
  if (!json.interests || !Array.isArray(json.interests)) {
    console.error('Interests API unexpected format:', json);
    return { interests: [] };
  }
  return json;
}
async function fetchOccupations(query) {
  const url = `https://ai.oigetit.com/AI71/Occupations?json=${encodeURIComponent(JSON.stringify({ Query: query }))}`;
  const res = await fetch(url);
  if (!res.ok) {
    console.error('Occupations API error:', res.status, await res.text());
    return { occupations: [] }; // fallback to empty
  }
  let json;
  try {
    json = await res.json();
  } catch (e) {
    console.error('Occupations API invalid JSON:', e);
    return { occupations: [] };
  }
  if (!json.occupations || !Array.isArray(json.occupations)) {
    console.error('Occupations API unexpected format:', json);
    return { occupations: [] };
  }
  return json;
}

// Fallback demo data
const DEMO_FALLBACK = {
  female: 42.3,
  male: 42.3,
  nonBinary: [
    { label: 'He/She', value: 14 },
    { label: 'He/Him', value: 14 },
    { label: 'They/Them', value: 14 },
    { label: 'They/She', value: 14 },
    { label: 'They/he', value: 14 }
  ],
  age: [
    { label: '18-24', value: 33.4 },
    { label: '25-34', value: 54.4 },
    { label: '35-44', value: 14.4 },
    { label: '45-54', value: 14.4 },
    { label: '55-64', value: 14.4 },
    { label: '65+', value: 14.4 }
  ]
};

export default function DemographicsPage({ query, filters = {} }) {
  const [gender, setGender] = useState({ female: 0, male: 0 });
  const [nonBinary, setNonBinary] = useState([]);
  const [age, setAge] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [interests, setInterests] = useState([]);
  const [occupations, setOccupations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchAll() {
      setLoading(true);
      setError(null);
      try {
        const [demo, langs, ints, occs] = await Promise.all([
          fetchDemographics(query).catch(err => {
            console.warn('Using fallback demo data for demographics:', err);
            return DEMO_FALLBACK;
          }),
          fetchLanguages(query),
          fetchInterests(query),
          fetchOccupations(query)
        ]);

        const safeDemo = demo || DEMO_FALLBACK;
        
        setGender({ female: safeDemo.female ?? DEMO_FALLBACK.female, male: safeDemo.male ?? DEMO_FALLBACK.male });
        setNonBinary(Array.isArray(safeDemo.nonBinary) && safeDemo.nonBinary.length > 0 ? safeDemo.nonBinary : DEMO_FALLBACK.nonBinary);
        setAge(Array.isArray(safeDemo.age) && safeDemo.age.length > 0 ? safeDemo.age : DEMO_FALLBACK.age);
        setLanguages((langs && langs.languages && Array.isArray(langs.languages)) ? langs.languages : []);
        setInterests((ints && ints.interests && Array.isArray(ints.interests)) ? ints.interests : []);
        setOccupations((occs && occs.occupations && Array.isArray(occs.occupations)) ? occs.occupations : []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchAll();
  }, [query]);

  return (
    <div style={{ padding: '2rem' }}>
      <h2>Demographics</h2>
      {loading && <div>Loading Demographics...</div>}
      {error && <div style={{ color: 'red' }}>Error loading demographics: {error}</div>}
      {!loading && !error && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '2rem' }}>
          <div style={{ background: '#fff', borderRadius: 8, padding: 24 }}>
            <h4>Gender</h4>
            <Doughnut
              data={{
                labels: ['Female', 'Male'],
                datasets: [{
                  data: [
                    filters?.gender?.includes('female') ? gender?.female ?? 0 : gender?.female ?? 0,
                    filters?.gender?.includes('male') ? gender?.male ?? 0 : gender?.male ?? 0,
                  ],
                  backgroundColor: ['#ba68c8', '#90caf9'],
                  borderWidth: 1
                }],
              }}
              options={{ cutout: '80%' }}
            />
          </div>
          <div style={{ background: '#fff', borderRadius: 8, padding: 24 }}>
            <h4>Non Binary Gender</h4>
            <Bar
              data={{
                labels: nonBinary
                  .filter(item => filters?.nonBinaryGender?.includes(item.label) || !filters?.nonBinaryGender || filters?.nonBinaryGender.length === 0)
                  .map(nb => nb.label)
                  .filter(label => label != null),
                datasets: [{
                  data: nonBinary
                    .filter(item => filters?.nonBinaryGender?.includes(item.label) || !filters?.nonBinaryGender || filters?.nonBinaryGender.length === 0)
                    .map(nb => nb.value)
                    .map(val => val ?? 0),
                  backgroundColor: ['#9575cd', '#4db6ac', '#ffd54f', 'ff8a65', '#a5d6a7'],
                  borderWidth: 1
                }],
              }}
              options={{ indexAxis: 'y', plugins: { legend: { display: false } } }}
            />
          </div>
          <div style={{ background: '#fff', borderRadius: 8, padding: 24 }}>
            <h4>Age</h4>
            <Bar
              data={{
                labels: age
                  .filter(item => filters?.age?.includes(item.label) || !filters?.age || filters?.age.length === 0)
                  .map(a => a.label)
                  .filter(label => label != null),
                datasets: [{
                  data: age
                    .filter(item => filters?.age?.includes(item.label) || !filters?.age || filters?.age.length === 0)
                    .map(a => a.value)
                    .map(val => val ?? 0),
                  backgroundColor: ['#f06292', '#64b5f6', '#81c784', '#ffd54f', '#ba68c8', '#90caf9'],
                  borderWidth: 1
                }],
              }}
              options={{ plugins: { legend: { display: false } } }}
            />
          </div>
          <div style={{ background: '#fff', borderRadius: 8, padding: 24 }}>
            <h4>Top Languages</h4>
            <Pie
              data={{
                labels: languages
                  .filter(item => filters?.languages?.includes(item.label) || !filters?.languages || filters?.languages.length === 0)
                  .map(l => l.label)
                  .filter(label => label != null),
                datasets: [{
                  data: languages
                    .filter(item => filters?.languages?.includes(item.label) || !filters?.languages || filters?.languages.length === 0)
                    .map(l => l.value)
                    .map(val => val ?? 0),
                  backgroundColor: ['#e57373', '#ffd54f', '#64b5f6', '#81c784', '#ba68c8', '#b0bec5'],
                  borderWidth: 1
                }],
              }}
              options={{ plugins: { legend: { position: 'bottom' } } }}
            />
          </div>
          <div style={{ background: '#fff', borderRadius: 8, padding: 24 }}>
            <h4>Top Interest</h4>
            <Bar
              data={{
                labels: interests
                  .filter(item => filters?.interests?.includes(item.label) || !filters?.interests || filters?.interests.length === 0)
                  .map(i => i.label)
                  .filter(label => label != null),
                datasets: [{
                  data: interests
                    .filter(item => filters?.interests?.includes(item.label) || !filters?.interests || filters?.interests.length === 0)
                    .map(i => i.value)
                    .map(val => val ?? 0),
                  backgroundColor: ['#e57373', '#ffd54f', '#64b5f6', '#81c784', '#ba68c8', '#b0bec5'],
                  borderWidth: 1
                }],
              }}
              options={{ indexAxis: 'y', plugins: { legend: { display: false } } }}
            />
          </div>
          <div style={{ background: '#fff', borderRadius: 8, padding: 24 }}>
            <h4>Top Occupations</h4>
            <Bar
              data={{
                labels: occupations
                  .filter(item => filters?.occupations?.includes(item.label) || !filters?.occupations || filters?.occupations.length === 0)
                  .map(o => o.label)
                  .filter(label => label != null),
                datasets: [{
                  data: occupations
                    .filter(item => filters?.occupations?.includes(item.label) || !filters?.occupations || filters?.occupations.length === 0)
                    .map(o => o.value)
                    .map(val => val ?? 0),
                  backgroundColor: ['#e57373', '#ffd54f', '#64b5f6', '#81c784', '#ba68c8', '#b0bec5'],
                  borderWidth: 1
                }],
              }}
              options={{ indexAxis: 'y', plugins: { legend: { display: false } } }}
            />
          </div>
        </div>
      )}
    </div>
  );
} 